<script setup lang="ts">
    const { headline = '' } = defineProps<{
        headline?: string;
    }>();

    const slots = useSlots();
    const hasTeaser = computed(() => Boolean(slots.teaser));

    const carouselMain = useTemplateRef<HTMLElement>('carouselMain');
    const { left, right } = useElementBounding(carouselMain);
    const { width: layoutViewportWidth } = useWindowSize({ includeScrollbar: false });

    const bleedLeft = computed(() => `${Math.max(0, left.value)}px`);

    const bleedRight = computed(() => {
        if (!right.value) {
            return '0px';
        }

        return `${Math.max(0, layoutViewportWidth.value - right.value)}px`;
    });
</script>

<template>
    <section
        class="trainings-carousel"
        :class="{ 'has-teaser': hasTeaser }"
        :style="{ '--carousel-measured-bleed-left': bleedLeft, '--carousel-bleed-right': bleedRight }"
    >
        <div
            v-if="hasTeaser"
            class="carousel-teaser"
        >
            <slot name="teaser" />
        </div>
        <div
            ref="carouselMain"
            class="carousel-main"
        >
            <div class="carousel-header">
                <h2
                    v-if="headline"
                    class="carousel-headline"
                >
                    {{ headline }}
                </h2>
            </div>
            <slot />
            <div
                v-if="slots.link"
                class="carousel-link"
            >
                <slot name="link" />
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
    .trainings-carousel {
        --carousel-teaser-width: 31rem;
        --carousel-header-offset: 0px;
        // measured value is set inline; derived here so the teaser layout below can override it
        --carousel-bleed-left: var(--carousel-measured-bleed-left, 0px);
        --carousel-bleed-right: 0px;

        position: relative;
        max-width: 100%;

        margin: var(--spacing--2) var(--spacing) 0 var(--spacing);

        @media (min-width: $br-l) {
            margin: var(--spacing--2) auto 0 auto;
            max-width: var(--fluid-max-width);
        }

        &.has-teaser {
            @media (min-width: $br-l) {
                --carousel-header-offset: var(--spacing--2);
                // the teaser sits to the left, so the track must not bleed over it
                --carousel-bleed-left: 0px;

                display: grid;
                grid-template-columns: var(--carousel-teaser-width) minmax(0, 1fr);
                gap: var(--spacing--2);
                align-items: start;
            }
        }
    }

    .carousel-teaser {
        margin-bottom: var(--spacing--2);

        @media (min-width: $br-l) {
            margin-bottom: 0;
        }
    }

    .carousel-main {
        position: relative;
        min-width: 0;
        padding-top: var(--carousel-header-offset);
        /* flex column so the button's align-self based alignment classes work */
        display: flex;
        flex-direction: column;
    }

    .carousel-link {
        margin-top: var(--spacing--2);
    }

    .carousel-header {
        display: flex;
        align-items: center;
        min-height: 3rem;
        margin-bottom: var(--spacing);
    }

    .carousel-headline {
        font-size: var(--font-l);
        font-weight: 700;
        padding-right: 0;
        margin: 0;

        @media (min-width: $br-m), (orientation: landscape) {
            /* keep clear of the absolutely positioned arrows */
            padding-right: 8rem;
        }
    }

    :deep(.splide) {
        position: static;
    }

    /*
     * bleed the track to both layout viewport edges (scrollbar-aware); the same
     * amounts are passed to Splide's padding option, so slides still start and
     * end at the content column
     */
    :deep(.carousel-splide) {
        margin-left: calc(-1 * var(--carousel-bleed-left));
        margin-right: calc(-1 * var(--carousel-bleed-right));
    }

    :deep(.splide__arrows) {
        display: none;

        @media (min-width: $br-m), (orientation: landscape) {
            position: absolute;
            transform: translateY(calc(-100% - var(--spacing)));
            right: 0;
            display: flex;
            gap: var(--spacing--05);
        }
    }

    :deep(.splide__arrow) {
        position: static;
        transform: none;
        width: 44px;
        height: 44px;
        border-radius: var(--border-radius);
        background: var(--black);
        color: var(--white);
        opacity: 1;
        transition: background var(--anim-duration-hover) var(--anim-ease-bounce);

        /* global font.scss sets color on every span, overriding the inherited button color */
        span.iconify {
            color: inherit;
        }
    }

    :deep(.splide__arrow:hover:not(:disabled)) {
        background: var(--brand-primary);
    }

    :deep(.splide__arrow:disabled) {
        opacity: 0.3;
    }

    :deep(.carousel-slide) {
        display: flex;
        justify-content: center;
    }
</style>
